import React from 'react';

/**
 * Utility functions for date formatting and validation
 * Used by VisualContentLoop component and related components
 */

/**
 * Helper function to format date as MM/DD/YYYY with time on a newline
 * @param {string|Date} dateString - The date string or Date object to format
 * @returns {JSX.Element|string} - Formatted date/time as JSX or empty string
 */
export const formatDate = (dateString) => {
	if (!dateString) return '';

	// Try to parse the date and format it
	try {
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return dateString; // Return original if invalid

		// Format date as MM/DD/YYYY
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const year = date.getFullYear();
		const dateStr = `${month}/${day}/${year}`;

		// Format time as H:MM AM/PM
		let hours = date.getHours();
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const ampm = hours >= 12 ? 'PM' : 'AM';
		hours = hours % 12;
		hours = hours ? hours : 12; // 0 should be 12
		const timeStr = `${hours}:${minutes} ${ampm}`;

		// Return JSX with date and time on separate lines
		return (
			<div style={{
				lineHeight: '1.4',
			}}>
				<div>{dateStr}</div>
				<div>{timeStr}</div>
			</div>
		);
	} catch (error) {
		return dateString; // Return original if parsing fails
	}
};

/**
 * Collapses an instant to local midnight on its calendar day, so two instants can
 * be compared by calendar day only.
 *
 * Built for the full UTC instants that scheduling stores (see `formatDateForJSON` in
 * utils/scheduling.js). Do NOT pass a bare `yyyy-mm-dd`: that parses as UTC midnight,
 * which lands on the previous local day anywhere west of UTC.
 *
 * @param {Date|string} value - Date object or a full date-time string
 * @returns {Date|null} - Local midnight on that day, or null if unparseable
 */
export const toLocalDateOnly = (value) => {
	// Reject nullish/empty up front: `new Date(null)` is the epoch, not an Invalid
	// Date, so an absent end_date would otherwise read as "1970" and skip everything.
	if (value === null || value === undefined || value === '') {
		return null;
	}

	const date = value instanceof Date ? value : new Date(value);
	if (isNaN(date.getTime())) {
		return null;
	}
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * True when `date` falls on an earlier calendar day than `other`.
 * An unparseable operand yields false — never hide content over a bad date.
 *
 * @param {Date|string} date
 * @param {Date|string} other
 * @returns {boolean}
 */
export const isBeforeLocalDay = (date, other) => {
	const a = toLocalDateOnly(date);
	const b = toLocalDateOnly(other);
	if (!a || !b) {
		return false;
	}
	return a.getTime() < b.getTime();
};

/**
 * True when `date` falls on a later calendar day than `other`.
 * An unparseable operand yields false — never hide content over a bad date.
 *
 * @param {Date|string} date
 * @param {Date|string} other
 * @returns {boolean}
 */
export const isAfterLocalDay = (date, other) => {
	const a = toLocalDateOnly(date);
	const b = toLocalDateOnly(other);
	if (!a || !b) {
		return false;
	}
	return a.getTime() > b.getTime();
};

/**
 * Helper function to check if an item's date range includes the selected date
 * @param {Object} item - The item with date range (can have startDate/endDate or scheduling object)
 * @param {string} selectedDate - The selected date to check against
 * @returns {boolean} - True if item is active on the selected date
 */
export const isItemActiveOnDate = (item, selectedDate) => {
	if (!selectedDate) {
		return true; // Show all items if no date selected
	}

	// Handle both transformed items (with startDate/endDate) and raw API items (with scheduling object)
	let endDate, startDate;

	if (item.startDate !== undefined || item.endDate !== undefined) {
		// Transformed item - extract dates individually
		startDate = item.startDate || null;
		endDate = item.endDate || null;
	} else if (item.scheduling) {
		// Raw API item - extract dates individually
		startDate = item.scheduling.start_date || null;
		endDate = item.scheduling.end_date || null;
	} else {
		return true; // Show item if no scheduling info at all
	}

	if (!startDate && !endDate) {
		return true; // Show item if no dates at all
	}

	try {
		// Parse the selected date as local time (yyyy-mm-dd format)
		let selected;
		if (selectedDate.includes('-')) {
			// Handle yyyy-mm-dd format by creating date in local timezone
			const [ year, month, day, ] = selectedDate.split('-').map(Number);
			selected = new Date(year, month - 1, day); // month is 0-indexed
		} else {
			selected = new Date(selectedDate);
		}

		// Normalize selected date to just compare year/month/day
		const selectedDateOnly = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());

		// Handle partial date ranges
		const hasStartDate = startDate && startDate !== '';
		const hasEndDate = endDate && endDate !== '';

		if (hasStartDate && hasEndDate) {
			// Both dates available - check if selected date is within range
			const itemStartDate = new Date(startDate);
			const itemEndDate = new Date(endDate);

			if (isNaN(itemStartDate.getTime()) || isNaN(itemEndDate.getTime())) {
				return true; // Show item if dates are invalid
			}

			const startDateOnly = new Date(itemStartDate.getFullYear(), itemStartDate.getMonth(), itemStartDate.getDate());
			const endDateOnly = new Date(itemEndDate.getFullYear(), itemEndDate.getMonth(), itemEndDate.getDate());

			return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;

		} else if (hasStartDate) {
			// Only start date - check if selected date is on or after start date
			const itemStartDate = new Date(startDate);

			if (isNaN(itemStartDate.getTime())) {
				return true; // Show item if start date is invalid
			}

			const startDateOnly = new Date(itemStartDate.getFullYear(), itemStartDate.getMonth(), itemStartDate.getDate());
			return selectedDateOnly >= startDateOnly;

		} else if (hasEndDate) {
			// Only end date - check if selected date is on or before end date
			const itemEndDate = new Date(endDate);

			if (isNaN(itemEndDate.getTime())) {
				return true; // Show item if end date is invalid
			}

			const endDateOnly = new Date(itemEndDate.getFullYear(), itemEndDate.getMonth(), itemEndDate.getDate());
			return selectedDateOnly <= endDateOnly;
		}

		return true; // Fallback - show item
	} catch (error) {
		return true; // Show item if parsing fails
	}
};

/**
 * Helper function to format a date as yyyy-mm-dd in LOCAL time.
 * @param {Date} [date] - Date to format; defaults to now.
 * @returns {string} - Date in yyyy-mm-dd format
 */
export const getCurrentDateString = (date = new Date()) => {
	return [
		date.getFullYear(),
		String(date.getMonth() + 1).padStart(2, '0'),
		String(date.getDate()).padStart(2, '0'),
	].join('-');
};

// A loop date is whatever <input type="date"> emits: exactly yyyy-mm-dd.
const LOOP_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True when the VCL loop date points at a day other than today, i.e. the user is
 * previewing rather than looking at the live loop.
 *
 * This is the condition that drives `skipTimeChecks` in `shouldSkipSlide`: on any
 * other day there is no meaningful "current time", so time-of-day windows must
 * not filter content. An empty/absent loop date means "today", not "preview".
 *
 * @param {string} loopDate - Date string in yyyy-mm-dd format
 * @returns {boolean}
 */
export const isPreviewingAnotherDay = (loopDate) => {
	return Boolean(loopDate) && loopDate !== getCurrentDateString();
};

/**
 * Synthesizes the "now" Date used to evaluate scheduling for a given loop date.
 *
 * - Falsy or malformed loopDate -> real now (treat it as "today").
 * - loopDate === today -> real now, so time-of-day rules still apply.
 * - Any other valid date -> LOCAL NOON on that date.
 *
 * Noon is deliberate, not arbitrary: callers read `getDay()` off this value, and
 * noon is the only hour that lands on the requested calendar day at every real
 * UTC offset. Midnight would shift the weekday for anyone east or west of UTC.
 *
 * @param {string} loopDate - Date string in yyyy-mm-dd format
 * @returns {Date} - Synthesized Date object, always a valid Date
 */
export const getNowForLoopDate = (loopDate) => {
	const now = new Date();

	if (!loopDate || !LOOP_DATE_PATTERN.test(loopDate)) {
		return now;
	}

	// Derive "today" from the same instant as `now`: two separate `new Date()`
	// calls can straddle midnight and misclassify today as a non-today date.
	if (loopDate === getCurrentDateString(now)) {
		return now;
	}

	const [ year, month, day, ] = loopDate.split('-').map(Number);
	const synthesized = new Date(year, month - 1, day, 12, 0, 0, 0);

	// Round-trip guard: out-of-range parts silently roll over rather than
	// producing an Invalid Date (2026-13-01 -> 2027-01-01, 2026-02-31 ->
	// 2026-03-03). An Invalid Date formats as NaN-NaN-NaN and fails this too.
	// Returning a wrong-but-valid date would silently mis-filter the loop.
	if (getCurrentDateString(synthesized) !== loopDate) {
		return now;
	}

	return synthesized;
};